import React, { useState, useCallback, useEffect, useMemo } from 'react';

function formatDateLabel(dateString) {
  if (!dateString) return '--';

  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;

  return [parts[2], parts[1], parts[0]].join('/');
}

function formatDayLabel(day) {
  if (!day) return '';

  return day.charAt(0).toUpperCase() + day.slice(1);
}

function parseSlotCount(value) {
  const parsedValue = parseInt(value, 10);

  return Number.isNaN(parsedValue) ? 50 : parsedValue;
}

function statusKind(status) {
  if (!status) return 'regular';

  const normalized = String(status).toLowerCase();

  if (normalized.includes('specific')) return 'specific';
  if (normalized.includes('holiday')) return 'holiday';

  return 'regular';
}

function scheduleKey(schedule) {
  return [
    schedule.hospital_id || '',
    schedule.clinic_name || '',
    schedule.day || '',
    schedule.from_time || '',
    schedule.to_time || '',
    schedule.schedule_status || ''
  ].join('::');
}

function SimrsSyncModal({ fetchUrl, syncUrl, csrfToken, adminUsername: expectedUsername, translations: t, defaultStartDate, defaultEndDate }) {
  const [step, setStep] = useState(1);
  const [stepDirection, setStepDirection] = useState('forward');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [previewData, setPreviewData] = useState(null);
  const [hospitalReports, setHospitalReports] = useState([]);
  const [deselected, setDeselected] = useState(() => new Set());
  const [adminUsername, setAdminUsername] = useState('');
  const [simulateTimeout, setSimulateTimeout] = useState(false);
  const [loading, setLoading] = useState(false);

  const diffDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
  const endBeforeStart = new Date(endDate) <= new Date(startDate);
  const exceedsLimit = diffDays > 7;
  const dateError = endBeforeStart ? t.date_order_error : (exceedsLimit ? t.date_range_error : null);
  const fetchDisabled = loading || !!dateError || !startDate || !endDate;
  const usernameMatch = adminUsername === expectedUsername;
  const selectedRangeLabel = `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;

  const selectedSchedules = useMemo(() => {
    if (!previewData) return [];

    return previewData.filter(schedule => !deselected.has(scheduleKey(schedule)));
  }, [previewData, deselected]);

  const saveDisabled = loading || !usernameMatch || selectedSchedules.length === 0;

  const closeModal = useCallback(() => {
    const modal = document.getElementById('modal-simrs-sync');

    if (modal && window.$) window.$(modal).modal('hide');
  }, []);

  const resetModal = useCallback(() => {
    setStep(1);
    setStepDirection('forward');
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setPreviewData(null);
    setHospitalReports([]);
    setDeselected(new Set());
    setAdminUsername('');
    setSimulateTimeout(false);
    setLoading(false);
  }, [defaultStartDate, defaultEndDate]);

  const goToStep = useCallback((nextStep) => {
    setStepDirection(nextStep > step ? 'forward' : 'backward');
    setStep(nextStep);
  }, [step]);

  const handleBackToChooseDates = useCallback(() => {
    setPreviewData(null);
    setHospitalReports([]);
    setDeselected(new Set());
    setAdminUsername('');
    setLoading(false);
    goToStep(1);
  }, [goToStep]);

  const initializeDeselection = useCallback((data) => {
    // Default: Holiday entries start unchecked because they cannot be
    // meaningfully synced as weekly slots; admins opt in if needed.
    const initial = new Set();

    data.forEach((schedule) => {
      if (statusKind(schedule.schedule_status) === 'holiday') {
        initial.add(scheduleKey(schedule));
      }
    });

    return initial;
  }, []);

  const handleFetch = useCallback(() => {
    setLoading(true);
    setPreviewData(null);
    setHospitalReports([]);
    setDeselected(new Set());
    setAdminUsername('');
    goToStep(2);

    fetch(fetchUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        from_date: startDate,
        to_date: endDate,
        schedule_type: 'executive',
        simulate_timeout: simulateTimeout ? '1' : '0'
      })
    })
      .then(res => res.json().then(data => ({ data, ok: res.ok })))
      .then(({ data, ok }) => {
        const fetchedData = Array.isArray(data.data) ? data.data : [];

        setLoading(false);

        if (ok && data.success) {
          setPreviewData(fetchedData);
          setHospitalReports(data.hospital_reports || []);
          setDeselected(initializeDeselection(fetchedData));
        } else {
          if (window.notice) window.notice(data.errors || t.fetch_failed, 'danger');
          closeModal();
        }
      })
      .catch(() => {
        setLoading(false);
        if (window.notice) window.notice(t.fetch_failed, 'danger');
        closeModal();
      });
  }, [fetchUrl, csrfToken, startDate, endDate, simulateTimeout, goToStep, t, closeModal, initializeDeselection]);

  const handleSave = useCallback(() => {
    if (simulateTimeout) {
      if (window.notice) window.notice(t.sync_timeout_error, 'danger');
      closeModal();
      return;
    }

    setLoading(true);

    fetch(syncUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        schedules: JSON.stringify(selectedSchedules),
        schedule_type: 'executive',
        admin_username: adminUsername,
        from_date: startDate,
        to_date: endDate
      })
    })
      .then(res => res.json().then(data => ({ data, ok: res.ok })))
      .then(({ data, ok }) => {
        setLoading(false);

        if (ok && data.success) {
          if (window.notice) window.notice(data.message, 'success');
          closeModal();
          setTimeout(() => window.Turbolinks.visit(), 1000);
        } else {
          if (window.notice) window.notice(data.errors || t.sync_failed, 'danger');
        }
      })
      .catch(() => {
        setLoading(false);
        if (window.notice) window.notice(t.sync_failed, 'danger');
      });
  }, [syncUrl, csrfToken, selectedSchedules, adminUsername, simulateTimeout, startDate, endDate, t, closeModal]);

  const toggleSchedule = useCallback((schedule) => {
    const key = scheduleKey(schedule);

    setDeselected((current) => {
      const next = new Set(current);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const toggleGroup = useCallback((group, shouldSelect) => {
    setDeselected((current) => {
      const next = new Set(current);

      group.schedules.forEach((schedule) => {
        const key = scheduleKey(schedule);

        if (shouldSelect) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });

      return next;
    });
  }, []);

  useEffect(() => {
    const modal = document.getElementById('modal-simrs-sync');

    if (modal && window.$) {
      window.$(modal).on('hidden.bs.modal', resetModal);
      return () => window.$(modal).off('hidden.bs.modal', resetModal);
    }
  }, [resetModal]);

  const previewGroups = [];

  if (previewData && previewData.length > 0) {
    const groupedPreview = {};

    previewData.forEach((schedule) => {
      const hospitalName = schedule.hospital_name || t.unknown_hospital;
      const clinicName = schedule.clinic_name || '';
      const unitId = schedule.unit_id || '';
      const groupKey = [hospitalName, clinicName, unitId].join('::');
      const slotCount = parseSlotCount(schedule.slots);

      if (!groupedPreview[groupKey]) {
        groupedPreview[groupKey] = {
          key: groupKey,
          hospitalName,
          clinicName,
          unitId,
          schedules: [],
          totalSlots: 0
        };
      }

      groupedPreview[groupKey].schedules.push({ ...schedule, slotCount });
      groupedPreview[groupKey].totalSlots += slotCount;
    });

    Object.values(groupedPreview).forEach((group) => {
      group.schedules.sort((firstSchedule, secondSchedule) => {
        const firstDay = parseInt(firstSchedule.day_integer, 10) || 0;
        const secondDay = parseInt(secondSchedule.day_integer, 10) || 0;

        if (firstDay !== secondDay) return firstDay - secondDay;

        return (firstSchedule.from_time || '').localeCompare(secondSchedule.from_time || '');
      });

      previewGroups.push(group);
    });

    previewGroups.sort((firstGroup, secondGroup) => {
      const hospitalCompare = firstGroup.hospitalName.localeCompare(secondGroup.hospitalName);

      if (hospitalCompare !== 0) return hospitalCompare;

      return firstGroup.clinicName.localeCompare(secondGroup.clinicName);
    });
  }

  const totalSelectedCount = selectedSchedules.length;
  const totalScheduleCount = previewData ? previewData.length : 0;

  const renderStepper = () => {
    const steps = [t.choose_date_range, t.fetch_data, t.confirm_save];

    return (
      <div className="stepper mt-3">
        {steps.map((label, index) => {
          const number = index + 1;
          const isCompleted = step > number;
          const isActive = step === number;
          const stepClass = 'stepper__step' + (isActive ? ' stepper__step--active' : '') + (isCompleted ? ' stepper__step--done' : '');

          return (
            <React.Fragment key={label}>
              {index > 0 && <div className={'stepper__line' + (step > index ? ' stepper__line--done' : '')} />}
              <div className={stepClass}>
                <span className="stepper__circle">{isCompleted ? <i className="zmdi zmdi-check" /> : number}</span>
                <span className="stepper__label">{label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderWarning = () => (
    <div className="alert alert-warning mb-4" role="alert">
      <div className="d-flex align-items-start">
        <i className="zmdi zmdi-alert-triangle mr-2 mt-1" />
        <div>
          <strong className="d-block mb-1">{t.warning}</strong>
          <span>{t.fetch_simrs_warning}</span>
        </div>
      </div>
    </div>
  );

  const renderDateFields = () => (
    <div className="simrs-sync__date-grid">
      <div className="simrs-sync__date-field">
        <label className="simrs-sync__date-label">{t.start_date}</label>
        <div className="form-group mb-0">
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={event => setStartDate(event.target.value)}
            disabled={loading}
          />
          <i className="form-group__bar" />
        </div>
      </div>

      <div className="simrs-sync__date-field">
        <label className="simrs-sync__date-label">{t.end_date}</label>
        <div className="form-group mb-0">
          <input
            type="date"
            className={dateError ? 'form-control is-invalid' : 'form-control'}
            value={endDate}
            onChange={event => setEndDate(event.target.value)}
            disabled={loading}
          />
          <i className="form-group__bar" />
        </div>
      </div>
    </div>
  );

  const renderPreviewSkeleton = () => (
    <div className="simrs-sync__preview-skeleton">
      {[1, 2].map((groupNumber) => (
        <div key={groupNumber} className="simrs-sync__preview-skeleton-card">
          <div className="simrs-sync__preview-skeleton-header">
            <div>
              <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--heading" />
              <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--subheading" />
            </div>
            <div className="simrs-sync__preview-skeleton-meta">
              <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--pill" />
              <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--pill" />
            </div>
          </div>
          {[1, 2, 3].map((rowNumber) => (
            <div key={rowNumber} className="simrs-sync__preview-skeleton-row">
              <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--day" />
              <div className="simrs-sync__preview-skeleton-row-side">
                <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--time" />
                <span className="simrs-sync__preview-skeleton-bar simrs-sync__preview-skeleton-bar--pill" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderStatusBadge = (status) => {
    const kind = statusKind(status);
    const labelMap = {
      regular: t.status_regular,
      specific: t.status_specific,
      holiday: t.status_holiday
    };

    return (
      <span className={'simrs-sync__status-badge simrs-sync__status-badge--' + kind}>
        {labelMap[kind] || labelMap.regular}
      </span>
    );
  };

  const renderSelectionSummary = () => {
    if (!previewData || previewData.length === 0) return null;

    return (
      <div className="simrs-sync__selection-summary">
        <i className="zmdi zmdi-check-square mr-1" />
        <span>{t.selected_summary.replace('%{selected}', totalSelectedCount).replace('%{total}', totalScheduleCount)}</span>
      </div>
    );
  };

  const renderBranchWarnings = () => {
    const skippedReports = hospitalReports.filter(report => report.status !== 'ok');

    if (skippedReports.length === 0) return null;

    return (
      <div className="alert alert-warning small mb-3" role="alert">
        <div className="d-flex align-items-start">
          <i className="zmdi zmdi-alert-triangle mr-2 mt-1" />
          <div>
            <strong className="d-block mb-1">{t.skipped_branches_title}</strong>
            <ul className="mb-0 pl-3">
              {skippedReports.map(report => (
                <li key={report.hospital_id}>
                  {report.hospital_name} — {report.message || (report.status === 'empty' ? t.branch_no_schedules : t.branch_fetch_error)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewPanel = () => {
    if (loading) return renderPreviewSkeleton();

    if (!previewData || previewData.length === 0) {
      return (
        <>
          {renderBranchWarnings()}
          <div className="simrs-sync__preview-empty">
            <div className="simrs-sync__empty">
              <div className="simrs-sync__empty-icon">
                <i className="zmdi zmdi-calendar-close" />
              </div>
              <div className="simrs-sync__empty-title">{t.no_schedules_found}</div>
              <div className="simrs-sync__empty-copy">{t.preview_placeholder_description}</div>
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="simrs-sync__preview-list">
        {renderBranchWarnings()}
        {renderSelectionSummary()}
        {previewGroups.map((group) => {
          const groupSelectedCount = group.schedules.filter(schedule => !deselected.has(scheduleKey(schedule))).length;
          const allSelected = groupSelectedCount === group.schedules.length;
          const noneSelected = groupSelectedCount === 0;

          return (
            <section key={group.key} className="simrs-sync__preview-card">
              <div className="simrs-sync__preview-card-header">
                <div className="simrs-sync__preview-card-header-main">
                  <label className="simrs-sync__group-toggle">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = !allSelected && !noneSelected;
                      }}
                      onChange={(event) => toggleGroup(group, event.target.checked)}
                    />
                    <span className="simrs-sync__group-toggle-box"><i className="zmdi zmdi-check" /></span>
                  </label>
                  <div>
                    <h6 className="simrs-sync__preview-card-title mb-1">{group.hospitalName}</h6>
                    <div className="simrs-sync__preview-card-meta">
                      {group.clinicName && <span className="simrs-sync__preview-card-clinic">{group.clinicName}</span>}
                      {group.unitId && <span className="simrs-sync__preview-card-unit">ID: {group.unitId}</span>}
                    </div>
                  </div>
                </div>
                <div className="simrs-sync__preview-card-summary">
                  <span className="simrs-sync__preview-card-stat">{group.schedules.length} {t.schedules_summary.toLowerCase()}</span>
                  <span className="simrs-sync__preview-card-stat">{group.totalSlots} {t.slots_unit}</span>
                </div>
              </div>

              <div className="simrs-sync__preview-rows">
                {group.schedules.map((schedule, index) => {
                  const key = scheduleKey(schedule);
                  const isSelected = !deselected.has(key);
                  const rowClasses = [
                    'simrs-sync__preview-row',
                    index < group.schedules.length - 1 ? 'simrs-sync__preview-row--bordered' : '',
                    !isSelected ? 'simrs-sync__preview-row--deselected' : ''
                  ].filter(Boolean).join(' ');

                  return (
                    <label key={group.key + '-' + index} className={rowClasses}>
                      <div className="simrs-sync__preview-row-main">
                        <span className="simrs-sync__row-toggle">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSchedule(schedule)}
                          />
                          <span className="simrs-sync__row-toggle-box"><i className="zmdi zmdi-check" /></span>
                        </span>
                        <span className="simrs-sync__preview-day">{formatDayLabel(schedule.day)}</span>
                        {renderStatusBadge(schedule.schedule_status)}
                      </div>
                      <div className="simrs-sync__preview-row-side">
                        <span className="simrs-sync__preview-time">{schedule.from_time} - {schedule.to_time}</span>
                        <span className="simrs-sync__slot-badge">{schedule.slotCount} {t.slots_unit}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  // const renderTimeoutCard = () => (
  //   <div className="card border mt-4">
  //     <div className="card-body p-3 d-flex align-items-center justify-content-between">
  //       <div className="d-flex align-items-center">
  //         <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ width: '36px', height: '36px', background: 'rgba(79, 195, 247, 0.15)' }}>
  //           <i className="zmdi zmdi-time-restore-setting" style={{ color: '#29b6f6', fontSize: '16px' }} />
  //         </div>
  //         <div>
  //           <strong style={{ fontSize: '0.875rem' }}>{t.simulate_timeout}</strong>
  //           <small className="d-block text-muted">{t.simulate_timeout_hint}</small>
  //         </div>
  //       </div>
  //       <div className="toggle-switch" style={{ marginBottom: 0 }}>
  //         <input
  //           type="checkbox"
  //           className="toggle-switch__checkbox"
  //           id="simrs-timeout-toggle"
  //           checked={simulateTimeout}
  //           onChange={event => setSimulateTimeout(event.target.checked)}
  //         />
  //         <i className="toggle-switch__helper" />
  //       </div>
  //     </div>
  //   </div>
  // );

  const renderConfirmSummary = () => (
    <div className="border rounded p-3 mb-3 d-flex align-items-center flex-wrap">
      <div className="simrs-sync-modal__header-icon mr-3">
        <i className="zmdi zmdi-calendar-check" />
      </div>
      <div className="flex-grow-1 mr-3">
        <small className="text-muted d-block">{t.selected_range}</small>
        <strong>{selectedRangeLabel}</strong>
      </div>
      <span className="simrs-sync__confirm-summary-badge">
        <i className="zmdi zmdi-check mr-1" />
        {t.selected_summary.replace('%{selected}', totalSelectedCount).replace('%{total}', totalScheduleCount)}
      </span>
    </div>
  );

  return (
    <div className="wizard-form simrs-sync">
      {renderStepper()}

      <div className="wizard-form__content">
        {step === 1 && (
          <div className={'wizard-form__step-content wizard-form__step-content--' + stepDirection}>
            <p className="text-muted mb-4">{t.choose_date_description}</p>

            {renderWarning()}
            {renderDateFields()}

            <p className={'small mt-3 mb-0 ' + (dateError ? 'text-danger' : 'text-muted')}>
              <i className={'zmdi mr-1 ' + (dateError ? 'zmdi-alert-circle' : 'zmdi-info-outline')} />
              {dateError || t.date_range_hint}
            </p>

            {/* {renderTimeoutCard()} */}

            <div className="wizard-form__footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal" disabled={loading}>
                {t.cancel}
              </button>
              <button type="button" className="btn btn-primary" disabled={fetchDisabled} onClick={handleFetch}>
                {t.fetch_data}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={'wizard-form__step-content wizard-form__step-content--' + stepDirection}>
            <p className="text-muted mb-4">{t.fetch_review_description}</p>

            {renderPreviewPanel()}

            <div className="wizard-form__footer">
              <button type="button" className="btn btn-secondary" onClick={handleBackToChooseDates} disabled={loading}>
                <i className="zmdi zmdi-arrow-left mr-1" />
                {t.back}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => goToStep(3)} disabled={loading || totalSelectedCount === 0}>
                {t.continue_to_confirm}
                {!loading && <i className="zmdi zmdi-arrow-right ml-1" />}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={'wizard-form__step-content wizard-form__step-content--' + stepDirection}>
            <p className="text-muted mb-4">{t.confirm_step_description}</p>

            {renderConfirmSummary()}

            <div>
              <label>{t.admin_username}</label>
              <div className="form-group mb-1">
                <input
                  type="text"
                  className={`form-control ${adminUsername && !usernameMatch ? 'is-invalid' : ''} ${usernameMatch ? 'is-valid' : ''}`}
                  placeholder={t.admin_username_placeholder}
                  value={adminUsername}
                  onChange={event => setAdminUsername(event.target.value)}
                  autoComplete="off"
                />
                <i className="form-group__bar" />
              </div>
              <small className={adminUsername && !usernameMatch ? 'text-danger' : 'text-muted'}>
                {adminUsername && !usernameMatch ? t.admin_username_mismatch : t.admin_username_hint}
              </small>
            </div>

            <div className="wizard-form__footer">
              <button type="button" className="btn btn-secondary" onClick={() => goToStep(2)} disabled={loading}>
                <i className="zmdi zmdi-arrow-left mr-1" />
                {t.back}
              </button>
              <button type="button" className="btn btn-primary" disabled={saveDisabled} onClick={handleSave}>
                {loading && <span className="spinner-border spinner-border-sm mr-1" />}
                {loading ? t.saving : t.confirm_save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SimrsSyncModal;
